
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { I18nModule, TranslationService } from '@spartacus/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { CustomLandingModule } from '../../custom-landing.module';

interface Cathode {
  id: string;
  brand: string;
  name: string;
  location: string;
  material: string;
  quantity: number;
  quantityUnit: string;
  price: string;
  currency: string;
  image: string;
  description: string;
  schedule: string;
  etad: string;
  pbs: string;
  accentColor: string;
}

interface Filter {
  id: string;
  name: string;
  code?: string;
}

interface CargoGroup {
  brand: string;
  location: string;
  accentColor: string;
  cargoes: Cathode[];
}

@Component({
  selector: 'app-bilateral-cargo-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectModule, I18nModule, SharedModule, CustomLandingModule],
  templateUrl: './bilateral-cargo-list.component.html',
  styleUrl: './bilateral-cargo-list.component.scss',
})
export class BilateralCargoListComponent implements OnInit {
  @ViewChildren('cargoCarousel') cargoCarousels!: QueryList<ElementRef<HTMLDivElement>>;

  title: string = '';
  navigationData: string = '';
  sidebar: boolean = true;
  searchText: string = '';
  showClear: boolean = false;
  enableLoader: boolean = false;

  listSrc: string = '/images/list-view.svg';
  gridSrc: string = '/images/grid-view-orange.svg';
  isListView: boolean = false;
  isGridView: boolean = true;

  selectedMaterial: string[] = [];
  selectedLocation: string[] = [];

  materialsData: Filter[] = [
    { id: '1', name: 'Copper cathode', code: 'CC' },
    { id: '2', name: 'Copper ore', code: 'CO' },
    { id: '3', name: 'Iron ore', code: 'IO' },
    { id: '4', name: 'Nickel', code: 'NI' },
    { id: '5', name: 'Cobalt', code: 'CB' }
  ];

  locationsData: Filter[] = [
    { id: '1', name: 'Olyda', code: 'OLY' },
    { id: '2', name: 'Esox', code: 'ESX' },
    { id: '3', name: 'Spence', code: 'SPN' },
    { id: '4', name: 'Port Adelaide', code: 'PAD' },
    { id: '5', name: 'Angamos', code: 'ANG' }
  ];

  cargoesData: Cathode[] = [
    this.createCargo('1', 'Olyda', 'ODEM: EW Copper cathode', 'Port Adelaide', 'Copper cathode', 'Aug 2026', '11/09/2026', '#00a3d7'),
    this.createCargo('2', 'Olyda', 'Copper cathodes Olyda Brand', 'Port Adelaide', 'Copper cathode', 'Sep 2026', '11/10/2026', '#00a3d7'),
    this.createCargo('3', 'Olyda', 'ERSTD: ER Copper cathode', 'Port Adelaide', 'Copper cathode', 'Oct 2026', '11/11/2026', '#00a3d7'),
    this.createCargo('4', 'Olyda', 'ERSTD: ER Copper cathode', 'Port Adelaide', 'Copper cathode', 'Nov 2026', '11/12/2026', '#00a3d7'),
    this.createCargo('5', 'Esox', 'Copper cathodes Esox Brand', 'Escondidia Antofagasta NB', 'Copper cathode', 'Aug 2026', '11/09/2026', '#9dca58'),
    this.createCargo('6', 'Esox', 'Copper cathode off grade Escondidit', 'Escondidia Antofagasta NB', 'Copper ore', 'Sep 2026', '11/10/2026', '#9dca58'),
    this.createCargo('7', 'Esox', 'Copper cathodes Esox Brand', 'Escondidia Antofagasta NB', 'Copper cathode', 'Oct 2026', '11/11/2026', '#9dca58'),
    this.createCargo('8', 'Esox', 'Copper cathodes Esox Brand', 'Escondidia Antofagasta NB', 'Copper cathode', 'Nov 2026', '11/12/2026', '#9dca58'),
    this.createCargo('9', 'Spence', 'Copper cathodes Spence brand', 'Angamos', 'Copper cathode', 'Aug 2026', '11/09/2026', '#d65ba3'),
    this.createCargo('10', 'Spence', 'Copper cathodes off grade Spence', 'Angamos', 'Copper ore', 'Sep 2026', '11/10/2026', '#d65ba3'),
    this.createCargo('11', 'Spence', 'Copper cathodes Spence brand', 'Angamos', 'Copper cathode', 'Oct 2026', '11/11/2026', '#d65ba3'),
    this.createCargo('12', 'Spence', 'Copper cathodes Spence brand', 'Angamos', 'Copper cathode', 'Nov 2026', '11/12/2026', '#d65ba3')
  ];

  filteredCargoesData: Cathode[] = [];

  get groupedCargoes(): CargoGroup[] {
    const groups = new Map<string, CargoGroup>();

    this.filteredCargoesData.forEach(cargo => {
      if (!groups.has(cargo.brand)) {
        groups.set(cargo.brand, {
          brand: cargo.brand,
          location: cargo.location,
          accentColor: cargo.accentColor,
          cargoes: []
        });
      }

      groups.get(cargo.brand)?.cargoes.push(cargo);
    });

    return Array.from(groups.values());
  }

  constructor(private translation: TranslationService) {}

  ngOnInit(): void {
    this.sidebar = true;
    this.filteredCargoesData = [...this.cargoesData];

    this.translation
      .translate('breadcrumb.bilateral.pageTitle')
      .subscribe(res => {
        this.title = res;
      });

    this.translation
      .translate('breadcrumb.bilateral.breadcrumb')
      .subscribe(res => {
        this.navigationData = res;
      });
  }

  toggleListView(): void {
    this.isListView = true;
    this.isGridView = false;
    this.listSrc = '/images/list-view-orange.svg';
    this.gridSrc = '/images/grid-view.svg';
  }

  toggleGridView(): void {
    this.isListView = false;
    this.isGridView = true;
    this.gridSrc = '/images/grid-view-orange.svg';
    this.listSrc = '/images/list-view.svg';
  }

  scrollRow(index: number, direction: 'left' | 'right'): void {
    const element = this.cargoCarousels?.toArray()[index]?.nativeElement;
    const scrollAmount = direction === 'right' ? 280 : -280;

    element?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  changeLocation(): void {
    this.updateClearState();
    this.applyFilters();
  }

  changeMaterial(): void {
    this.updateClearState();
    this.applyFilters();
  }

  applyFilters(): void {
    const searchValue = this.searchText?.toLowerCase();

    this.filteredCargoesData = this.cargoesData.filter(cargo => {
      const matchLocation = !this.selectedLocation?.length ||
        this.selectedLocation.some(location =>
          cargo.brand.toLowerCase().includes(location.toLowerCase()) ||
          cargo.location.toLowerCase().includes(location.toLowerCase())
        );

      const matchMaterial = !this.selectedMaterial?.length ||
        this.selectedMaterial.some(material => cargo.material.toLowerCase().includes(material.toLowerCase()));

      const matchSearch = !searchValue ||
        cargo.name.toLowerCase().includes(searchValue) ||
        cargo.brand.toLowerCase().includes(searchValue) ||
        cargo.material.toLowerCase().includes(searchValue);

      return matchLocation && matchMaterial && matchSearch;
    });
  }

  search(): void {
    this.applyFilters();
  }

  clearAllFilters(): void {
    this.selectedLocation = [];
    this.selectedMaterial = [];
    this.searchText = '';
    this.showClear = false;
    this.filteredCargoesData = [...this.cargoesData];
  }

  omitSpecialChar(event: KeyboardEvent): boolean {
    return event.charCode !== 46;
  }

  placeBid(cargo: Cathode): void {
    console.log('Place bid for cargo:', cargo);
  }

  viewDetails(cargo: Cathode): void {
    console.log('View details for cargo:', cargo);
  }

  private createCargo(
    id: string,
    brand: string,
    name: string,
    location: string,
    material: string,
    schedule: string,
    etad: string,
    accentColor: string
  ): Cathode {
    return {
      id,
      brand,
      name,
      location,
      material,
      quantity: 1000,
      quantityUnit: 'MT',
      price: '1000-2000',
      currency: 'USD',
      image: '/images/product-copper.jpg',
      description: `${name} from ${brand}`,
      schedule,
      etad,
      pbs: '5/35',
      accentColor
    };
  }

  private updateClearState(): void {
    this.showClear = !!this.selectedLocation?.length || !!this.selectedMaterial?.length;
  }
}
